import type { CreateRevisionRequest, DocumentVersion, RichText } from '@core/types/document';
import { richTextToPlain } from '@/components/document/editor/richtext-convert';
import { documentTarget } from './complianceLogic';

export type SnapshotError = 'invalidSnapshot' | 'invalidDocument' | 'invalidContent' | 'invalidBlock';
export type BlockDiffKind = 'added' | 'removed' | 'changed' | 'moved' | 'unchanged';

export interface DiffDocumentBlock {
  id: string;
  parentId: string | null;
  type: string;
  text: string;
  position: string;
  order: number;
  isAttachment: boolean;
}

export interface ParsedVersionSnapshot {
  document: Record<string, unknown> | null;
  blocks: DiffDocumentBlock[];
  error: SnapshotError | null;
}

export interface BlockDiffEntry {
  id: string;
  kind: BlockDiffKind;
  changed: boolean;
  moved: boolean;
  oldBlock?: DiffDocumentBlock;
  newBlock?: DiffDocumentBlock;
}

export interface VersionDiffSummary {
  added: number;
  removed: number;
  changed: number;
  moved: number;
  unchanged: number;
}

export interface DocumentVersionDiff {
  entries: BlockDiffEntry[];
  summary: VersionDiffSummary;
  oldSnapshot: ParsedVersionSnapshot;
  newSnapshot: ParsedVersionSnapshot;
}

type UnknownRecord = Record<string, unknown>;

const ATTACHMENT_TYPES = new Set([
  'BLOCK_TYPE_IMAGE',
  'BLOCK_TYPE_VIDEO',
  'BLOCK_TYPE_FILE',
  'BLOCK_TYPE_AUDIO',
]);

function decoded(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function record(value: unknown): UnknownRecord | null {
  const valueDecoded = decoded(value);
  return valueDecoded !== null && typeof valueDecoded === 'object' && !Array.isArray(valueDecoded)
    ? valueDecoded as UnknownRecord
    : null;
}

function richText(value: unknown): string {
  if (!Array.isArray(value)) return '';
  const parts = value.filter((part) => part && typeof part === 'object').map((part) => ({
    ...part,
    plain_text: part.plain_text ?? part.plainText ?? part.text?.content ?? '',
  })) as RichText[];
  return richTextToPlain(parts);
}

function blockText(block: UnknownRecord): string {
  const parts = [
    richText(block.rich_text ?? block.richText),
    richText(block.caption),
  ];
  const equation = record(block.equation);
  if (equation?.expression) parts.push(String(equation.expression));

  const file = record(block.file);
  if (file?.name) parts.push(String(file.name));

  const bookmark = record(block.bookmark);
  if (bookmark?.title) parts.push(String(bookmark.title));
  if (bookmark?.description) parts.push(String(bookmark.description));

  const childPage = record(block.child_page ?? block.childPage);
  if (childPage?.title) parts.push(String(childPage.title));

  const tableRow = record(block.table_row ?? block.tableRow);
  if (Array.isArray(tableRow?.cells)) {
    const cells = tableRow.cells.map((cell: unknown) => richText(record(cell)?.rich_text ?? record(cell)?.richText));
    if (cells.some(Boolean)) parts.push(cells.join(' | '));
  }

  return parts.filter(Boolean).join('\n');
}

function normalizeBlocks(content: unknown[]): DiffDocumentBlock[] {
  const blocks: DiffDocumentBlock[] = [];
  const seen = new Set<string>();

  const visit = (value: unknown, inheritedParent: string | null) => {
    const block = record(value);
    if (!block) throw new Error('invalid block');
    const id = record(block.entity)?.id;
    if (typeof id !== 'string' || !id) throw new Error('invalid block id');
    if (seen.has(id)) return;
    const children = block.children;
    if (children != null && !Array.isArray(children)) throw new Error('invalid block children');

    seen.add(id);
    if (block.is_deleted === true) return;

    const parentId = typeof block.parent_block_id === 'string'
      ? block.parent_block_id
      : typeof block.parentBlockId === 'string'
        ? block.parentBlockId
        : inheritedParent;
    const type = typeof block.type === 'string' && block.type ? block.type : 'BLOCK_TYPE_UNSPECIFIED';
    const normalized: DiffDocumentBlock = {
      id,
      parentId,
      type,
      text: blockText(block),
      position: typeof block.position === 'string' ? block.position : '',
      order: blocks.length,
      isAttachment: ATTACHMENT_TYPES.has(type),
    };
    blocks.push(normalized);
    for (const child of children ?? []) visit(child, id);
  };

  for (const block of content) visit(block, null);
  return blocks;
}

export function parseVersionSnapshot(snapshot: unknown): ParsedVersionSnapshot {
  const value = record(snapshot);
  if (!value) return { document: null, blocks: [], error: 'invalidSnapshot' };

  const document = record(value.document);
  if (!document) return { document: null, blocks: [], error: 'invalidDocument' };

  const content = decoded(value.content);
  const nestedBlocks = record(content)?.blocks;
  const rawBlocks = Array.isArray(content)
    ? content
    : Array.isArray(nestedBlocks)
      ? nestedBlocks
      : null;
  if (!rawBlocks) return { document, blocks: [], error: 'invalidContent' };

  try {
    return { document, blocks: normalizeBlocks(rawBlocks), error: null };
  } catch {
    return { document, blocks: [], error: 'invalidBlock' };
  }
}

export function diffDocumentBlocks(oldBlocks: DiffDocumentBlock[], newBlocks: DiffDocumentBlock[]): BlockDiffEntry[] {
  const oldById = new Map(oldBlocks.map((block) => [block.id, block]));
  const newById = new Map(newBlocks.map((block) => [block.id, block]));
  const entries: BlockDiffEntry[] = [];

  for (const newBlock of newBlocks) {
    const oldBlock = oldById.get(newBlock.id);
    if (!oldBlock) {
      entries.push({ id: newBlock.id, kind: 'added', changed: false, moved: false, newBlock });
      continue;
    }

    const changed = oldBlock.type !== newBlock.type || oldBlock.text !== newBlock.text;
    const moved = oldBlock.parentId !== newBlock.parentId
      || (oldBlock.position || newBlock.position
        ? oldBlock.position !== newBlock.position
        : oldBlock.order !== newBlock.order);
    entries.push({
      id: newBlock.id,
      kind: changed ? 'changed' : moved ? 'moved' : 'unchanged',
      changed,
      moved,
      oldBlock,
      newBlock,
    });
  }

  for (const oldBlock of oldBlocks) {
    if (!newById.has(oldBlock.id)) {
      entries.push({ id: oldBlock.id, kind: 'removed', changed: false, moved: false, oldBlock });
    }
  }
  return entries;
}

export function summarizeVersionDiff(entries: BlockDiffEntry[]): VersionDiffSummary {
  return entries.reduce<VersionDiffSummary>((summary, entry) => {
    summary[entry.kind] += 1;
    if (entry.kind === 'changed' && entry.moved) summary.moved += 1;
    return summary;
  }, { added: 0, removed: 0, changed: 0, moved: 0, unchanged: 0 });
}

export function diffVersionSnapshots(oldSnapshot: unknown, newSnapshot: unknown): DocumentVersionDiff {
  const oldParsed = parseVersionSnapshot(oldSnapshot);
  const newParsed = parseVersionSnapshot(newSnapshot);
  const entries = oldParsed.error || newParsed.error ? [] : diffDocumentBlocks(oldParsed.blocks, newParsed.blocks);
  return {
    entries,
    summary: summarizeVersionDiff(entries),
    oldSnapshot: oldParsed,
    newSnapshot: newParsed,
  };
}

export function buildRestoreRevisionRequest(
  documentId: string,
  productId: string,
  version: Pick<DocumentVersion, 'version_number'>,
  changeSummary?: string,
): CreateRevisionRequest & { source_version: number } {
  return {
    target: documentTarget(documentId, productId),
    source_version: version.version_number,
    change_summary: changeSummary || undefined,
  };
}
